import { ToastType } from '@components';
import { openDialogBox } from '@store/dialogBox';
import { ILoadComments, changeCommentVisibility } from '@store/orders';
import type { ChangeTrackingSource } from '@typeDefinitions/changeTrackingTypes';
import { handleToast, isAdmin, isSuperAdmin } from '@utils/utils';
import { DELETE_TRACKING_COMMENT_DIALOG_ID } from '@views/order/constants';
import { DateTime } from 'luxon';
import { Show } from 'solid-js';

const tempatureInitials = (unitOfTemperature: string | null) => {
  if (unitOfTemperature === 'Celsius') {
    return 'C';
  }
  return 'F';
};

type CommentProps = {
  comment: ILoadComments;
  onCommentClick: (comment: ILoadComments) => void;
  setDeleteRow: (id: number) => void;
  deletedComments: number[];
  setDeletedComments: (value: number[]) => void;
  changeTrackingSource?: ChangeTrackingSource;
};

export const Comment = (props: CommentProps) => {
  return (
    <Show when={!props.deletedComments.includes(props.comment.id)}>
      <tr class="hover:bg-gray-50 border-b border-gray-200">
        <td
          class="high-res:max-w-[500px] netbook:max-w-[220px] align-top cursor-pointer py-2"
          onClick={() => props.onCommentClick(props.comment)}
        >
          <div class="flex flex-col">
            <div class="font-bold text-sm">{props.comment.name}</div>
            <div class="wrap-break-word whitespace-pre-line">
              {props.comment.comment}
            </div>
          </div>
        </td>
        <td class="w-[200px]">
          <div class="flex items-center">
            <input
              type="checkbox"
              checked={props.comment.visibleToCustomer}
              class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded-sm focus:ring-blue-500"
              onChange={async () => {
                try {
                  await changeCommentVisibility(
                    props.comment.id,
                    props.changeTrackingSource,
                  );
                } catch (error) {
                  handleToast(ToastType.Error, (error as Error).message);
                }
              }}
            />
            <label class="ml-2 text-sm font-medium text-gray-900">
              Visible to Customer
            </label>
          </div>
        </td>
        <td class="w-[200px] align-top cursor-pointer relative">
          <div class="flex flex-col absolute top-0 left-0 right-0 bottom-0 justify-center">
            <div class="font-bold text-xs">
              {Boolean(props.comment.timestamp)
                ? DateTime.fromISO(props.comment.timestamp, {
                    zone: 'utc',
                  })
                    .setZone('local')
                    .toFormat('MMMM dd yyyy HH:mm a')
                    .toUpperCase()
                : ''}
            </div>
            <div>
              <Show when={props.comment.city}>
                {props.comment.city}, {props.comment.state}
              </Show>
            </div>
          </div>
        </td>
        <td>
          <Show when={props.comment.temperature}>
            {`${props.comment.temperature} ${tempatureInitials(
              props.comment.unitOfTemperature,
            )}`}
          </Show>
        </td>
        <td>
          <Show when={isAdmin() || isSuperAdmin()} fallback={<></>}>
            <button
              class="p-1 hover:bg-gray-100 rounded-full"
              onClick={() => {
                props.setDeleteRow(props.comment.id);
                openDialogBox(DELETE_TRACKING_COMMENT_DIALOG_ID);
              }}
            >
              <div class="relative w-5 h-5 text-red-500 font-bold">✕</div>
            </button>
          </Show>
        </td>
      </tr>
    </Show>
  );
};
